'use client'

import { useRouter } from 'next/navigation'
import { useEffect, useId, useRef } from 'react'
import { toast } from 'sonner'
import { authClient } from '~/lib/auth.client'

interface TelegramLoginProps {
  align?: 'left' | 'center' | 'right'
  onSuccess?: () => void
  onError?: (error: Error) => void
}

export function TelegramLogin({
  align = 'center',
  onSuccess,
  onError,
}: TelegramLoginProps) {
  const router = useRouter()
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetId = useId().replace(/:/g, '_')
  const iframeRef = useRef<HTMLIFrameElement | null>(null)

  useEffect(() => {
    if (!containerRef.current) {
      console.error('Container ref is not available')
      return
    }

    // Обработчик сообщений от iframe
    const handleMessage = async (event: MessageEvent) => {
      // Проверяем, что сообщение от нашего виджета
      if (event.data?.type === 'telegram-login-success' && event.data?.widgetId === widgetId) {
        // Показываем уведомление об успешном входе
        toast.success('Вход выполнен успешно!', {
          description: 'Страница обновляется...',
        })

        // Вызываем callback успеха сразу
        onSuccess?.()

        // Даем время серверу установить cookie сессии
        await new Promise(resolve => setTimeout(resolve, 300))

        // Принудительно обновляем сессию через authClient
        // Это триггерит все подписки на изменение сессии
        await authClient.getSession()

        // Обновляем роутер для перерендера серверных компонентов
        router.refresh()

        // Дополнительно делаем "мягкую" перезагрузку страницы
        // Это гарантирует, что все компоненты получат новое состояние
        setTimeout(() => {
          window.location.href = window.location.pathname + window.location.search
        }, 500)
      }
      else if (event.data?.type === 'telegram-login-error' && event.data?.widgetId === widgetId) {
        onError?.(new Error(event.data.error || 'Authentication failed'))
      }
    }

    window.addEventListener('message', handleMessage)

    // Очищаем контейнер перед добавлением нового iframe
    containerRef.current.innerHTML = ''

    // Создаем iframe для изоляции виджета
    const queryParams = new URLSearchParams({
      id: widgetId,
      align,
    })

    const iframe = document.createElement('iframe')
    iframe.src = `/api/widgets/telegram-login?${queryParams.toString()}`
    iframe.style.border = 'none'
    iframe.style.display = 'inline-block'
    iframe.style.overflow = 'hidden'
    iframe.style.backgroundColor = 'transparent'
    iframe.style.height = '44px'
    iframe.setAttribute('title', 'Telegram Login')
    iframe.setAttribute('allow', 'clipboard-read; clipboard-write')

    iframeRef.current = iframe
    containerRef.current.appendChild(iframe)

    return () => {
      window.removeEventListener('message', handleMessage)
      if (containerRef.current && iframeRef.current) {
        containerRef.current.removeChild(iframeRef.current)
      }
    }
  }, [align, onSuccess, onError, router, widgetId])

  return <div ref={containerRef} className="inline-flex" />
}
